import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CssProps } from '../models/css.props';
import { ThemeModel } from '../models/theme.model';
import { ConsoleLogger } from '../shared/console-logger';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private selectedThemeUrl: string;
  public defaultThemeUrl =
    'https://fss-user-uploads.azureedge.net/user-uploads/production/fssadmin/dd/themes';
  public _currentTheme: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
  public currentTheme$: Observable<any> = this._currentTheme.asObservable();

  public themes: Record<string, ThemeModel> = {
    midnight: { themeName: this.defaultThemeUrl + '/MidnightBlue/MidnightBlue.json' },
    undersea: { themeName: this.defaultThemeUrl + '/UnderTheSea/UnderTheSea.json' },
    orange: { themeName: this.defaultThemeUrl + '/ColorOrange/ColorOrange.json' },
    blue: { themeName: this.defaultThemeUrl + '/ColorBlue/ColorBlue.json' },
    space: { themeName: this.defaultThemeUrl + '/Space/Space.json' },
    tech: { themeName: this.defaultThemeUrl + '/Technology/Technology.json' },
    darkGreen: { themeName: this.defaultThemeUrl + '/ColorDarkGreen/ColorDarkGreen.json' },
    darkOrange: { themeName: this.defaultThemeUrl + '/ColorDarkOrange/ColorDarkOrange.json' },
    green: { themeName: this.defaultThemeUrl + '/ColorGreen/ColorGreen.json' },
    grey: { themeName: this.defaultThemeUrl + '/ColorGrey/ColorGrey.json' },
    purple: { themeName: this.defaultThemeUrl + '/ColorPurple/ColorPurple.json' }
  };

  constructor(private http: HttpClient) {
  }


  public setCurrentTheme(theme: any) {
    this._currentTheme.next(theme);
  }

  public getCurrentTheme$(): Observable<object> {
    return this.currentTheme$;
  }

  public getCurrentTheme(): object {
    return this._currentTheme.getValue();
  }

  public setTheme(name: string = 'midnight') {
    const theme: ThemeModel = this.themes[name];
    if (theme && theme.themeName) {
      this.loadThemeIntoDocument(theme.themeName);
    } else {
      this.loadThemeIntoDocument();
    }
  }

  public loadThemeIntoDocument(themeUrl: string = this.themes['midnight'].themeName) {
    this.selectedThemeUrl = themeUrl;
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache'
    });

    const currentTheme: any = {};
    this.http.get(themeUrl, { headers })
      .subscribe((data: any) => {
        ConsoleLogger.debug(`Loading theme: ${JSON.stringify(themeUrl)}`);
        data = data as Record<string, CssProps>;
        for (const item of Object.keys(data)) {
          for (const property of Object.keys(data[item])) {
            if (property !== 'native' && item !== 'mainBackground') {
              document.documentElement.style.setProperty(`--${item}-${property}`, data[item][property]);
              currentTheme[`--${item}-color`] = data[item][property];
            }
          }

          if (item === 'mainBackground'){
            let value = data[item]['color'];
            if (Object.keys(data[item]).indexOf('imageLinks') > -1) {
              const imageLinks = data[item]['imageLinks'];
              value = `url(${imageLinks?.webImage})`;
            } else if (Object.keys(data[item]).indexOf('gradientColor') > -1){
              value = `linear-gradient(${data[item]['color']}, ${data[item]['gradientColor']})`;
            }
            currentTheme[`--${item}-color`] = value;
            document.documentElement.style.setProperty(`--${item}-color`, value);
          }
        }
        this.setCurrentTheme(currentTheme);
      });
  }

  public isMidnightTheme() {
    return this.selectedThemeUrl === this.themes['midnight'].themeName;
  }

  public isADarkTheme() {
    return this.selectedThemeUrl === this.themes['midnight'].themeName ||
      this.selectedThemeUrl === this.themes['darkGreen'].themeName ||
      this.selectedThemeUrl === this.themes['darkOrange'].themeName;
  }

}
